<script>
import {defineComponent} from "vue";
import {useRoute, useRouter} from "vue-router";
import {
  campaignStatusActive,
  campaignStatusEnded,
  campaignStatusUpcoming,
} from "../../stores/campaign";
import PageHeader from "../components/landing/PageHeader.vue";
import {useLandingPageStore} from "../../stores/landingPage";
import DonationInfo from "../components/landing/DonationInfo.vue";
import DonationProgressBar from "../components/DonationProgressBar.vue";
import {ApplicationHelper} from "../../helpers/ApplicationHelper";
import LoadingSpinner from "../components/loadingSpinner.vue";
import {useDonationSessionStore} from "../../stores/donationSession";
import Drawer from "../components/Drawer.vue";
import Icon from "../components/Icon.vue";
import PageFooter from "../components/landing/PageFooter.vue";
import Popup from "../components/popup.vue";
import GiftBasketSideBarButton from "./GiftBasketSidebarButton.vue";
import DonationInput from "../donation/DonationInput.vue";
import ThankYouAnimation from "../animations/ThankYou.vue";
import BaseModal from "../components/BaseModal.vue";
import {useAnalyticEventStore} from "../../stores/analyticEvents";
import DonorWall from "../donation/DonorWall.vue";

export default defineComponent({
  name: "DonatableShowPage",
  components: {
    DonorWall,
    BaseModal, ThankYouAnimation,
    DonationInput,
    GiftBasketSideBarButton,
    Popup,
    PageFooter,
    Icon,
    Drawer,
    LoadingSpinner,
    DonationProgressBar,
    DonationInfo,
    PageHeader

  },
  props: {},
  setup() {
    const route = useRoute();
    const router = useRouter();
    const landingStore = useLandingPageStore();
    const sessionStore = useDonationSessionStore();
    const analyticEventStore = useAnalyticEventStore();

    return {
      route,
      router,
      landingStore,
      sessionStore,
      analyticEventStore,
    };
  },
  data() {
    return {
      customization: null,
      loading: false,
      campaign: {},
      donationAmount: 0,
      donationError: false,
      addingToBasket: false,
      popupMessage: "",
      donationSuccessful: false,
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    organizationId() {
      return this.landingStore.organizationId;
    },

    organization() {
      return this.landingStore.organization;
    },

    brand() {
      return this.landingStore.localBrand;
    },

    percentFunded() {
      if (!this.campaign.donation_target) {
        return;
      }
      const percent = Math.floor((this.campaign.all_time_contribution_total / this.campaign.donation_target) * 100);
      if (percent > 100) {
        return 100; // Make Sure it doesnt go over 100
      } else {
        return percent;
      }
    },
    pageData() {
      return this.landingStore.publishedLayout?.pages?.find((page) => page.page_type === 'landing_page');
    },
    cssProps() {
      return {
        '--color-primary': this.landingStore.localBrand.color_primary,
        '--color-primary--dark': this.landingStore.localBrand.color_primary_dark,
        '--color-primary--light': this.landingStore.localBrand.color_primary_light,
        '--color-primary-text': this.landingStore.localBrand.color_primary_text,

        '--donation-bar-base-color': '#FFFFFF',
        '--donation-bar-fill-color': this.landingStore.localBrand.color_primary,
        '--donation-bar-fill-width': '50%'
      };
    },
    suggestedAmounts() { // TODO
      if (!this.campaign.donation_suggestions_enabled || !this.campaign.donation_suggestions) {
        return [];
      } else {
        return this.campaign.donation_suggestions;
      }
    },
    campaignLoading() {
      return this.loading && !this.campaign.name;
    },
    campaignActive() {
      if (this.campaignLoading) return true; // show the skeleton loader
      if (this.campaign.model === "Campaign" && this.campaign.status === campaignStatusActive) {
        return true;
      }
      return this.campaign.model === "Fund";

    },
    formattedDescription() {
      return this.campaign?.description.split('\n');
    },
    footerValues() {
      const footer = this.pageData?.components?.find(component => component.component_type === 'footer');
      return this.landingStore.getComponentValues(footer);
    },

  },
  watch: {
    donationAmount(value) {
      if (value > 0) {
        this.donationError = false;
      }
    },
    'sessionStore.donationSession.completed'(newValue, oldValue) {
      if (newValue && !oldValue) {
        // Reload campaign / fund when a successful donation goes through
        this.landingStore.getDonatableBySlug(this.route.params.slug)
          .then((donatable) => {
            this.campaign = donatable;
          });
      }
    }
  },

  mounted() {

    if (this.organizationId) {
      this.analyticEventStore.setOrganizationId(this.organizationId);
      this.analyticEventStore.logEvent({ action: 'donatable-page-visit' });
    }

    this.loading = true;

    if (this.route.params.org && !this.landingStore.organizationId) {
      this.landingStore.setOrganizationId(this.route.params.org);
    }

    return this.landingStore.loadPublishedPage()
      .then(() => this.landingStore.getDonatableBySlug(this.route.params.slug))
      .then((campaign) => {
        this.campaign = campaign;
        if (this.organization) {
          this.analyticEventStore.setOrganizationId(this.landingStore.organization.id);
        }
        if (!this.campaign) {
          return this.navigateToLandingPage();
        }
        // Check if the URL contains source=qr query parameter
        if (this.route.query.source === 'qr') {
          this.trackQr();
        }
      })
      .catch(() => {
        this.navigateToLandingPage();
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    // Silently reloads the campaign to update amount raised, resets donation form, etc
    onDonationSuccess(payload) {
      const isImmediateCheckout = !!this.sessionStore.immediateCheckoutDonation;
      if (!isImmediateCheckout) {
        this.sessionStore.emptyCart();
      } else {
        this.sessionStore.resetState();
      }

      // Signed-in donor: redirect to donor dashboard after brief delay
      const userSignedIn = payload?.userSignedIn || false;
      if (userSignedIn) {
        this.donationSuccessful = true;
        const org = this.$route.params.org || this.sessionStore.organizationId;
        setTimeout(() => {
          window.location.href = `/${org}/donor/dashboard/overview?donated=true`;
        }, 2000);
        return;
      }

      // Anonymous: show heart animation then redirect to landing page
      this.donationSuccessful = true;
      this.donationAmount = 0;

      setTimeout(() => {
        this.donationSuccessful = false;
        this.$router.push({ name: 'landing' });
      }, 3000);
    },

    validateDonationAmount() {
      const amount = this.sessionStore.dollarsToCents(this.donationAmount);
      if (amount >= 500) {
        return true;
      } else {
        this.donationError = true;
        return false;
      }
    },

    addDonationToCart() {
      if (this.validateDonationAmount() && !this.addingToBasket) {
        // Converts to whole number in cents from currency input
        const amount = this.sessionStore.dollarsToCents(this.donationAmount);
        const donation = {
          amount: amount,
          selectedTarget: this.campaign,
          type: "OneTimeDonation"
        };

        this.addingToBasket = true;
        setTimeout(() => {
          // Animation Timeout, prevents double clicks
          this.sessionStore.addDonationToCart(donation);
          this.addingToBasket = false;
          this.showPopup("Gift Added!");
        }, 100);

        this.analyticEventStore.logEvent({
          action: `donatable-show-add-to-cart`,
          traceable_type: this.campaign.model,
          traceable_id: this.campaign.id,
          metadata: {
            donation
          }
        });
      }
    },

    singleDonationCheckout(event) {
      if (this.validateDonationAmount() && !this.addingToBasket) {
        const amount = this.sessionStore.dollarsToCents(this.donationAmount);
        const donationType = event.type === 'recurring' ? "RecurringDonation" : "OneTimeDonation";
        const donation = {
          amount: amount,
          selectedTarget: this.campaign,
          type: donationType,
          frequency: event.interval
        };

        this.addingToBasket = true;
        setTimeout(() => {
          // Animation Timeout, prevents double clicks
          this.sessionStore.singleDonationCheckout(donation);
          this.addingToBasket = false;
          this.$refs.giftBasket.checkout();
          this.$refs.giftBasket.openBasket();
        }, 800);

        this.analyticEventStore.logEvent({
          action: `donatable-show-single-donation-checkout`,
          traceable_type: this.campaign.model,
          traceable_id: this.campaign.id,
          metadata: {
            donation
          }
        });
      }
    },

    suggestedAmountOnClick(amount) {
      this.donationAmount = amount;
    },

    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    },

    navigateToLandingPage() {
      return this.router.push({
        name: "landing",
        params: {
          org: this.route.params.org
        }
      });
    },
    campaignStatusUpcoming() {
      return campaignStatusUpcoming;
    },
    campaignStatusEnded() {
      return campaignStatusEnded;
    },
    trackQr() {
      const url = window.location.href;

      this.analyticEventStore.logEvent({
        action: 'donatablepage-qr-visit',
        traceable_type: this.campaign.model,
        traceable_id: this.campaign.id,
        metadata: {
          timestamp: new Date().toISOString(),
          organization: this.organization,
          destination: url,
          source: 'QR Code',
        }
      });
    },
  }
});

</script>

<template>
  <div
    class="campaign-page-container max-w-screen overflow-hidden"
    :style="cssProps"
  >
    <page-header
      :org-name="organization?.name"
      :logo-resource="brand?.logo"
      :show-logo="pageData?.header?.values?.logo?.value"
      :donate-button-background-color="brand.color_primary"
      :donate-button-hover-background-color="brand.color_primary_dark"
      @logo-click="navigateToLandingPage"
    >
      <template #right-icon-slot>
        <gift-basket-side-bar-button
          v-if="!campaignLoading"
          ref="giftBasket"
          @donation-success="onDonationSuccess"
          @close="sessionStore.resetImmediateCheckout"
        />
      </template>
    </page-header>

    <div class="outer-container flex-grow">
      <div class="inner-container grid grid-cols-1 lg:grid-cols-3 gap-3">
        <!-- Mobile Order: Cashout first -->
        <div class="block lg:hidden">
          <div
            v-if="campaignActive"
            class="donation-input-container"
          >
            <div
              v-if="campaignLoading"
              class="loading-skeleton w-full h-72 rounded-sm "
            />
            <donation-input
              v-else
              :donation-amount="donationAmount"
              :suggested-amounts="suggestedAmounts"
              :subscription-intervals="landingStore.subscriptionIntervals"
              :subscriptions-enabled="landingStore.subscriptionsEnabled"
              @add-to-cart="addDonationToCart"
              @checkout="singleDonationCheckout"
              @update:donation-amount="val => donationAmount = val"
            />
          </div>
        </div>

        <!-- Campaign Info -->
        <div class="md:col-span-2">
          <div
            v-if="campaignLoading"
            class="loading-skeleton w-full h-screen rounded-sm "
          />

          <div
            v-else
            class="campaign-info-container"
          >
            <div class="campaign-name">
              {{ campaign.name }}
            </div>

            <div class="mt-4 mb-6">
              <!-- Display full donation info with bar -->
              <donation-info
                v-if="campaign.donation_display_style === 'bar'"
                :curr-donations-total="campaign.all_time_contribution_total"
                :donation-goal="campaign.donation_target"
                :text-color="'#000'"
                :donation-bar-base-color="'#F3F2F2'"
                :donation-bar-fill-color="brand.color_accent"
                :end-date="campaign.end_date"
                :show-bar="true"
                :show-amount="true"
              />

              <div
                v-if="campaign.donation_display_style === 'bar'"
                class="mt-2 flex flex-row gap-4 body-2-light text-right justify-end"
              >
                <div v-if="campaign.total_contributions > 0">
                  <span>{{ campaign.total_contributions }} Gift</span>
                  <span v-if="campaign.total_contributions > 1">s</span>
                </div>
                <div v-if="campaign.total_donors > 0">
                  <span>{{ campaign.total_donors }} Donor</span>
                  <span v-if="campaign.total_donors > 1">s</span>
                </div>
              </div>

              <!-- Custom layout for 'amount' setting -->
              <div
                v-else-if="campaign.donation_display_style === 'amount'"
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
              >
                <div class="text-2xl font-bold">
                  {{ ApplicationHelper.formatMoney(campaign.all_time_contribution_total, true, "$0.00") }} Raised
                </div>

                <div class="flex flex-row gap-4 body-2-light text-right sm:ml-auto">
                  <div v-if="campaign.total_contributions> 0">
                    <span>{{ campaign.total_contributions }} Gift</span>
                    <span v-if="campaign.total_contributions > 1">s</span>
                  </div>
                  <div v-if="campaign.total_donors> 0">
                    <span>{{ campaign.total_donors }} Donor</span>
                    <span v-if="campaign.total_donors > 1">s</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-section-content-container">
              <div class="campaign-banner-wrapper">
                <img
                  v-if="campaign.banner_image_url"
                  class="campaign-banner-image"
                  :src="campaign.banner_image_url"
                  onload="this.style.opacity=1"
                >
              </div>

              <div
                v-if="campaign.description"
                class="info-section-body-text-container body-1-regular"
              >
                <p
                  v-for="(p, i) in formattedDescription"
                  :key="i"
                >
                  {{ p }}
                </p>
              </div>
            </div>
          </div>

          <!-- Donor wall on mobile below campaign info -->
          <div
            v-if="campaign.show_donor_wall"
            class="donor-wall-container mt-4 md:hidden"
          >
            <donor-wall
              :key="campaign?.donor_wall?.toString()"
              :donation-data="campaign.donor_wall"
            />
          </div>
        </div>

        <div class="right-content-container hidden lg:block">
          <div
            v-if="campaignActive"
            class="donation-input-container"
          >
            <div
              v-if="campaignLoading"
              class="loading-skeleton w-full h-72 rounded-sm "
            />
            <donation-input
              v-else
              :donation-amount="donationAmount"
              :suggested-amounts="suggestedAmounts"
              :subscription-intervals="landingStore.subscriptionIntervals"
              :subscriptions-enabled="landingStore.subscriptionsEnabled"
              @add-to-cart="addDonationToCart"
              @checkout="singleDonationCheckout"
              @update:donation-amount="val => donationAmount = val"
            />
          </div>
          <div
            v-else-if="campaign.status === campaignStatusEnded"
            class="campaign-invalid-message-container"
          >
            <div>
              This campaign ended on {{
                ApplicationHelper.formatDate(campaign.end_date, {
                  time: false,
                  weekday: false
                })
              }}
            </div>
            <div
              role="button"
              class="button min-h-[50px] mt-4"
              @click="navigateToLandingPage"
            >
              See more campaigns
            </div>
          </div>
          <div
            v-else-if="campaign.status === campaignStatusUpcoming()"
            class="campaign-invalid-message-container"
          >
            <div>
              This campaign starts on {{
                ApplicationHelper.formatDate(campaign.start_date, {
                  time: false,
                  weekday: false
                })
              }}
            </div>

            <div
              role="button"
              class="button min-h-[50px] mt-4"
              @click="navigateToLandingPage"
            >
              See more campaigns
            </div>
          </div>

          <div
            v-if="campaign.show_donor_wall"
            class="donor-wall-container mt-4"
          >
            <donor-wall
              :key="campaign"
              :donation-data="campaign.donor_wall"
            />
          </div>
        </div>
      </div>
    </div>
    <page-footer
      v-bind="footerValues"
      :layout="footerValues?.showFooter ? 'contact' : 'basic'"
      :logo-src="footerValues?.logo ? organization.logo_url : null"
      :organization="organization"
      :background-color="landingStore.getFooterBackgroundColor()"
      :text-color="landingStore.getContrastTextColor(landingStore.getFooterBackgroundColor())"
      disable-navigation
    />
  </div>

  <popup
    ref="popup"
    :message="popupMessage"
    :duration="1400"
  />

  <base-modal
    v-if="donationSuccessful"
    :key="brand"
    :style="cssProps"
  >
    <div class="text-center">
      <h1 class="text-4xl font-bold">
        Thank You!
      </h1>
      <div class="text-lg font-semibold text-gray-600">
        Your donation has been received
      </div>

      <div class="m-4">
        <thank-you-animation
          :full-page="false"
          size="small"
        />
      </div>
    </div>
  </base-modal>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.campaign-info-container {
  background-color: white;
  border-radius: $radius-rounded;
  box-shadow: $shadow-card;
  padding: $space-lg;

  .campaign-name {
    font-size: $section-heading-size;
    font-weight: $section-heading-weight;
    line-height: $section-heading-line;
  }

  @screen md {
    padding: $space-3xl;
  }

  .info-section-content-container {

    justify-content: space-between;
    align-items: flex-start;
    height: 100%;
    width: 100%;
    gap: $space-2xl;
    flex-direction: row-reverse;


    .info-section-body-text-container {
      margin-top: $space-xl;

      p:not(:first-child) {
        margin-top: $space-lg;
      }
    }

    .campaign-banner-wrapper {
      width: 100%;
      margin-bottom: $space-xl;

      .campaign-banner-image {
        min-height: 300px;
        max-height: 350px;
        width: 100%;
        object-fit: cover;
        display: block;
        border-radius: $radius-rounded;
        opacity: 0;
        transition: opacity 0.4s ease;
        margin: 0 auto;
      }
    }

    @media screen and (max-width: $breakpoint-max-lg) {
      &.info-section-content-container {
        flex-direction: column;

        &.image-position--left {
          flex-direction: column-reverse;
        }
      }
    }
  }
}


button, .button {
  @apply w-full rounded font-semibold text-lg text-center content-center;

  background-color: var(--color-primary);
  color: var(--color-primary-text);

  &:hover {
    background-color: var(--color-primary--dark);
  }

  :deep(.circle-progress) {
    .circle-progress-fill-line {
      stroke: var(--color-primary-text);
    }
  }

  &.confirm-button {
    @apply rounded-full w-auto p-4;
  }

  &.decline-button {
    @apply w-auto underline;
    background-color: white;
    color: var(--color-primary);

    &:hover {
      color: var(--color-primary--dark);
    }
  }
}

#suggested-amounts-container {
  @apply mt-1 mb-2 gap-3;

  .suggested-amount {
    @apply w-full p-2 rounded-lg text-center cursor-pointer;
    @apply font-semibold text-xl bg-white;
    border: 2px solid var(--color-primary);

    transition: background-color 0.3s, color 0.3s, border 0.3s;

    &:hover {
      background-color: var(--color-primary);
      color: var(--color-primary-text);
    }
  }
}

.campaign-invalid-message-container {
  @apply text-xl p-12;
  background-color: white;
  border-radius: $radius-rounded;
  box-shadow: $shadow-card;
}

.donation-bar-container {
  .donation-bar {
    width: 100%;
    height: 26px;
    background-color: var(--donation-bar-base-color);
    border-radius: $radius-full;
    border: 1px solid var(--donation-bar-fill-color);

    .donation-bar-fill {
      height: 100%;
      width: var(--donation-bar-fill-width);
      border-radius: $radius-md;
      background-color: var(--donation-bar-fill-color);
    }
  }
}

.loading-spinner-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.donation-success-container {
  @apply flex flex-row justify-center mt-4;
  background-color: white;
  border-radius: $radius-rounded;
  box-shadow: $shadow-card;
  max-width: 600px;
  padding: $space-xl;
}

.campaign-page-container {
  min-height: 100vh;
  overflow-y: auto !important;
  height: 100vh;
  display: flex;
  flex-direction: column;

  :deep(#landing-page-header) {
    min-height: 92px;
  }

  // setup container widths
  .outer-container {
    background-color: $color-section-bg;
    width: 100%;
    display: flex;
    justify-content: center;

    :deep(.inner-container) {
      width: 100%;
      max-width: $content-max-width;
      margin: $space-lg;
      padding: 0 $space-md;
    }
  }

}

.loading-skeleton {
  @apply bg-gray-300 animate-pulse;
  border-radius: $radius-rounded;
}


</style>
